import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { ArrowLeft, Radio, Eye, Loader2, Lock, Heart, Unlock, X, Coins } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const DEFAULT_UNLOCK_HOURS = 1;

/**
 * LiveCategoryRoom — displays all active LIVE streams for a given category (e.g. "private_room", "couple_room", "concert_room").
 * Categories in LOCKED_LIVE_CATEGORIES are LOCKED: tapping a stream shows an unlock modal with server-side pricing.
 * 70% of the fee goes to host as Tokwin, 30% to platform.
 */
export default function LiveCategoryRoom({ category, title, subtitle, icon: Icon = Radio, accent = "#FF2A5F", testIdPrefix }) {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlockTarget, setUnlockTarget] = useState(null); // { ...stream, cost, hours }
  const [unlocking, setUnlocking] = useState(false);
  const [unlockErr, setUnlockErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get(`/live/active?category=${encodeURIComponent(category)}`)
        .then(r => { if (!cancelled) { setStreams(Array.isArray(r.data) ? r.data : []); setLoading(false); } })
        .catch(() => { if (!cancelled) setLoading(false); });
    };
    load();
    const t = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [category]);

  const openStream = async (s) => {
    try {
      const { data } = await api.get(`/live/${s.id}/unlock-status`);
      if (data.unlocked) {
        nav(`/live/watch/${s.id}`);
      } else {
        setUnlockTarget({ ...s, unlock_cost: data.cost_goldly, unlock_hours: data.duration_hours || DEFAULT_UNLOCK_HOURS });
      }
    } catch {
      nav(`/live/watch/${s.id}`);
    }
  };

  const confirmUnlock = async () => {
    if (!unlockTarget) return;
    setUnlockErr(""); setUnlocking(true);
    try {
      await api.post(`/live/${unlockTarget.id}/unlock`);
      const sid = unlockTarget.id;
      setUnlockTarget(null);
      nav(`/live/watch/${sid}`);
    } catch (e) { setUnlockErr(formatErr(e)); }
    finally { setUnlocking(false); }
  };

  return (
    <div className="px-4 py-5 pb-24" data-testid={`${testIdPrefix}-page`}>
      <div className="flex items-center gap-3 mb-1">
        <button onClick={() => nav(-1)} data-testid={`${testIdPrefix}-back`} className="p-2 rounded-full bg-white/8"><ArrowLeft className="w-4 h-4"/></button>
        <div className="flex-1 min-w-0">
          <h1 className="font-outfit font-extrabold text-2xl leading-tight flex items-center gap-2">
            <Icon className="w-6 h-6 flex-shrink-0" style={{ color: accent }}/>
            {title}
          </h1>
          {subtitle && <p className="text-white/60 text-xs">{subtitle}</p>}
        </div>
        <button onClick={() => nav("/live")} data-testid={`${testIdPrefix}-golive`}
          className="px-3 py-2 rounded-full font-outfit font-extrabold text-xs flex items-center gap-1 active:scale-95"
          style={{ background: `linear-gradient(90deg, ${accent}, #A855F7)`, color: "white" }}>
          <Radio className="w-3.5 h-3.5"/> Go Live
        </button>
      </div>

      <div className="p-3 rounded-2xl border mb-4 text-[11px] text-white/85 flex items-start gap-2"
           style={{ background: "linear-gradient(90deg, #FFD70011, #FF2A5F11)", borderColor: "#FFD70044" }}>
        <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#FFD700" }}/>
        <div>
          <div className="font-outfit font-bold text-white mb-0.5">Premium locked category 🔒</div>
          Tap any streamer to unlock their room with <b style={{ color: "#FFD700" }}>Goldly</b> (server-set price · {DEFAULT_UNLOCK_HOURS} hour access). 70% goes to the host, 30% to the platform.
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }}/></div>
      ) : streams.length === 0 ? (
        <div className="text-center py-16 text-white/55">
          <Icon className="w-14 h-14 mx-auto mb-2 opacity-40" style={{ color: accent }}/>
          <p className="font-outfit font-bold text-lg">No {title.toLowerCase()}s live right now</p>
          <p className="text-xs mt-1">Be the first to go live in this category!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3" data-testid={`${testIdPrefix}-list`}>
          {streams.map(s => (
            <button key={s.id} onClick={() => openStream(s)} data-testid={`${testIdPrefix}-card-${s.id}`}
              className="relative aspect-[9/12] rounded-2xl overflow-hidden border border-white/10 active:scale-95 transition group"
              style={{ background: `linear-gradient(135deg, ${accent}55, #A855F733, #00E5FF33)` }}>
              {s.mocked && s.mocked_url ? (
                <video src={s.mocked_url} muted autoPlay loop playsInline className="absolute inset-0 w-full h-full object-cover filter blur-md brightness-50"/>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon className="w-10 h-10 text-white/35"/>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/85"/>
              {/* Big central lock overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center z-[2]">
                <div className="w-14 h-14 rounded-full bg-black/60 border-2 border-[#FF7A00] flex items-center justify-center shadow-[0_0_24px_rgba(255,215,0,0.55)]">
                  <Lock className="w-7 h-7 text-[#FF7A00]"/>
                </div>
                <div className="mt-2 px-2 py-0.5 rounded-full bg-black/60 text-[9px] font-black text-[#FF7A00] uppercase tracking-wider">Tap to unlock</div>
              </div>
              <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide z-[3]" style={{ background: accent, color: "white" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/>Live
              </div>
              <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] flex items-center gap-1 z-[3]"><Eye className="w-3 h-3"/>{s.viewers || 0}</div>
              <div className="absolute bottom-0 inset-x-0 p-2.5 z-[3]">
                <div className="flex items-center gap-1.5 mb-1">
                  <img src={s.user?.avatar} className="w-6 h-6 rounded-full border border-white/30" alt=""/>
                  <span className="text-xs font-bold truncate">@{s.user?.username || "creator"}</span>
                </div>
                <p className="text-xs leading-tight line-clamp-2">{s.title}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Unlock modal */}
      <AnimatePresence>
        {unlockTarget && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-black/75" onClick={() => setUnlockTarget(null)}/>
            <motion.div initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring", damping:24}}
              data-testid={`${testIdPrefix}-unlock-modal`}
              className="relative w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl bg-gradient-to-br from-[#1A0F2E] to-[#0B0F19] border border-[#FF7A00]/40 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-[#FF7A00]/20 border border-[#FF7A00]/50 flex items-center justify-center">
                    <Unlock className="w-4 h-4 text-[#FF7A00]"/>
                  </div>
                  <h3 className="font-outfit font-extrabold text-lg">Unlock this {title}</h3>
                </div>
                <button onClick={() => setUnlockTarget(null)} data-testid={`${testIdPrefix}-unlock-close`} className="p-1.5 rounded-full bg-white/8"><X className="w-4 h-4"/></button>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 mb-3">
                <img src={unlockTarget.user?.avatar} className="w-11 h-11 rounded-full object-cover border-2" style={{ borderColor: accent }} alt=""/>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm truncate">@{unlockTarget.user?.username}</div>
                  <div className="text-[11px] text-white/60 truncate">{unlockTarget.title}</div>
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-gradient-to-br from-[#FF7A00]/10 to-[#FF2A5F]/10 border border-[#FF7A00]/30 mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wider text-white/60 font-bold">Unlock cost</span>
                  <span className="flex items-center gap-1 text-[#FF7A00] font-outfit font-black text-lg">
                    <Coins className="w-4 h-4"/>{(unlockTarget.unlock_cost || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wider text-white/60 font-bold">Access duration</span>
                  <span className="text-white font-outfit font-bold">{unlockTarget.unlock_hours || DEFAULT_UNLOCK_HOURS} hour</span>
                </div>
              </div>

              <div className="text-[10px] text-white/50 mb-3 leading-relaxed">
                70% ({Math.round((unlockTarget.unlock_cost || 0) * 0.7).toLocaleString()} Goldly worth) goes to the host as Tokwin.<br/>
                30% ({Math.round((unlockTarget.unlock_cost || 0) * 0.3).toLocaleString()} Goldly) goes to the platform.<br/>
                Unlock is one-time per session — leaving and rejoining within {unlockTarget.unlock_hours || DEFAULT_UNLOCK_HOURS}h stays free.
              </div>

              {unlockErr && <div className="text-[#FF2A5F] text-xs mb-2" data-testid={`${testIdPrefix}-unlock-err`}>{unlockErr}</div>}

              <div className="flex gap-2">
                <button onClick={() => setUnlockTarget(null)} data-testid={`${testIdPrefix}-unlock-cancel`}
                  className="flex-1 py-3 rounded-full bg-white/8 border border-white/15 text-white/85 font-outfit font-bold active:scale-95">Cancel</button>
                <button onClick={confirmUnlock} disabled={unlocking} data-testid={`${testIdPrefix}-unlock-confirm`}
                  className="flex-1 py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-black flex items-center justify-center gap-1 disabled:opacity-50 active:scale-95">
                  {unlocking ? <Loader2 className="w-4 h-4 animate-spin"/> : <Unlock className="w-4 h-4"/>}
                  Unlock for {(unlockTarget.unlock_cost || 0).toLocaleString()}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* Category-specific default-export wrappers — used by App.js routes */
export function PrivateChatRoom() {
  return <LiveCategoryRoom
    category="private_room"
    title="Hot videos and live chat"
    subtitle="Locked · Unlock with 15,000 Goldly for 1 hour"
    icon={Lock}
    accent="#A855F7"
    testIdPrefix="private-room"
  />;
}

export function CoupleChatRoom() {
  return <LiveCategoryRoom
    category="couple_room"
    title="Couple Chat Room"
    subtitle="Locked · Unlock with 15,000 Goldly for 1 hour"
    icon={Heart}
    accent="#FF2A5F"
    testIdPrefix="couple-room"
  />;
}

export function ConcertRoom() {
  return <LiveCategoryRoom
    category="concert_room"
    title="Concert / DJ / Fashion Room"
    subtitle="Live performances · Unlock with Goldly for 1 hour"
    icon={Radio}
    accent="#FFD700"
    testIdPrefix="concert-room"
  />;
}
